import type { ReactNode } from "react";

import type {
  OfferActionPlacementProps,
  OfferPlacementKey,
  OfferPlacementProps,
} from "@/components/OfferPlacement.checkout";
import { ActionCheckout, ContinuationCheckout } from "@/components/OfferPlacement.checkout-lazy";
import DepthGate from "@/components/OfferPlacement.gate";
import {
  CollectInputsPlacement,
  type CollectInputsPlacementProps,
} from "@/components/OfferPlacement.collect-inputs";
import type { KundaliResponse } from "@/lib/api/kundali";

export type {
  OfferActionPlacementProps,
  OfferPlacementContext,
  OfferPlacementKey,
  OfferPlacementProps,
} from "@/components/OfferPlacement.checkout";
export type { CollectInputsPlacementProps } from "@/components/OfferPlacement.collect-inputs";

/**
 * The one offer component. Every report offer on the site mounts through it:
 *
 *  - the report-continuation card (kundali result, chat): `primaryReport` props;
 *  - a caller-rendered checkout or link (dosha calculators, matching): a
 *    render-prop child that receives the validated contract;
 *  - `mode="collect_inputs"`: a page that cannot check out yet routes the
 *    seeker to the birth-details form, carrying the placement as
 *    `origin_placement`.
 *
 * `gate` adds the masked paid-depth preview above any of them. This file has no
 * "use client": the collect-inputs mode stays server-rendered, and the checkout
 * modes load their client boundary as a separate chunk, so a calculator page
 * that only ever bridges never ships the placement runtime in its first load.
 */

export function OfferPlacement<K extends OfferPlacementKey>(
  props: OfferActionPlacementProps<K>
): ReactNode;
export function OfferPlacement(props: OfferPlacementProps): ReactNode;
export function OfferPlacement(props: CollectInputsPlacementProps): ReactNode;
export function OfferPlacement<K extends OfferPlacementKey>(
  props: OfferPlacementProps | OfferActionPlacementProps<K> | CollectInputsPlacementProps
): ReactNode {
  if ("mode" in props && props.mode === "collect_inputs") {
    return <CollectInputsPlacement {...props} />;
  }
  const checkout = props as OfferPlacementProps | OfferActionPlacementProps<K>;
  const action = typeof checkout.children === "function";
  const Checkout = (action ? ActionCheckout : ContinuationCheckout) as unknown as (
    checkoutProps: OfferPlacementProps | OfferActionPlacementProps<K>
  ) => ReactNode;
  const placement = <Checkout {...checkout} />;
  if (!checkout.gate) return placement;
  // The gate renders synchronously, so the preview is on the first paint.
  const planId = action
    ? (props as OfferActionPlacementProps<K>).planId
    : (props as OfferPlacementProps).primaryReport.planId;
  return (
    <DepthGate planId={planId} language={checkout.language ?? "en"}>
      {placement}
    </DepthGate>
  );
}

export function strongestPresentDosha(
  doshas: KundaliResponse["doshas"]
): KundaliResponse["doshas"][number] | null {
  const active = (doshas ?? []).filter((d) => d.is_present);
  if (!active.length) return null;
  return [...active].sort((a, b) => b.severity - a.severity)[0] ?? null;
}
